import { SimpleError } from '../types/costwand.d';
import { displayGlobalError } from '../utils/globalErrorHandler.ts'; // NEW: Import global error handler

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Helper function to check if the current environment is development.
 * This centralizes the environment check.
 */
const isDevelopmentEnv = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Wrapper for console.log that only logs in the development environment.
 */
const logDev = (...args: any[]) => {
  if (isDevelopmentEnv()) {
    console.log(...args);
  }
};

/**
 * Wrapper for console.warn that only logs in the development environment.
 */
const warnDev = (...args: any[]) => {
  if (isDevelopmentEnv()) {
    console.warn(...args);
  }
};

/**
 * Wrapper for console.error that only logs in the development environment.
 * Errors should generally always be logged, but for consistency with the request,
 * we'll apply the dev check. However, in a real app, `console.error` might be
 * desired in all environments for critical issues. For this request, we'll
 * make it dev-only.
 */
const errorDev = (...args: any[]) => {
  if (isDevelopmentEnv()) {
    console.error(...args);
  }
};

/**
 * Custom Error for API responses.
 */
export class ApiError extends Error {
  status: number;
  errorDetail?: SimpleError;
  constructor(status: number, message: string, detail?: SimpleError) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorDetail = detail;
  }
}

const MAX_RETRIES = 3; // Maximum number of retries for 429 errors
const DEFAULT_RETRY_AFTER_MS = 1000; // Default retry delay in milliseconds if header is missing or invalid

// --- Global Rate Limiting and Throttling ---
const MIN_REQUEST_INTERVAL_MS = 300; // Minimum delay between any API calls (0.3 seconds)
let lastRequestInitiationTime = 0; // Timestamp when the last request was *allowed to start*
let globalThrottleUntilTime = 0; // Timestamp until which all requests should be paused due to a 429

// --- Request Queueing ---
// A function that performs the API call and returns a Promise
type QueuedRequestFunction = () => Promise<any>;

// The queue stores the request function along with its resolve/reject callbacks
const requestQueue: Array<{
  requestFn: QueuedRequestFunction;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];
let isProcessingQueue = false; // Flag to ensure only one queue processor runs at a time

/**
 * Ensures a minimum delay between API calls and handles global throttling.
 * This function should be called *before* initiating a fetch request from the queue.
 */
async function applyGlobalRateLimit() {
  const now = Date.now();
  const nextAvailableTime = Math.max(
    lastRequestInitiationTime + MIN_REQUEST_INTERVAL_MS,
    globalThrottleUntilTime
  );

  if (now < nextAvailableTime) {
    const delay = nextAvailableTime - now;
    logDev(`[Queue] Delaying next request by ${delay}ms to respect MIN_REQUEST_INTERVAL_MS or globalThrottleUntilTime.`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  // Update time for the current request, now that it's allowed to start
  lastRequestInitiationTime = Date.now();
}

/**
 * Processes requests from the queue one by one, respecting rate limits.
 */
async function processQueue() {
  if (isProcessingQueue) {
    return; // Already processing the queue
  }
  isProcessingQueue = true;
  logDev("[Queue] Starting to process queue.");

  while (requestQueue.length > 0) {
    // Dequeue the next request
    const { requestFn, resolve, reject } = requestQueue.shift()!;

    // Apply global rate limit before executing the *next* request from the queue
    await applyGlobalRateLimit();

    try {
      logDev(`[Queue] Executing request from queue. Remaining: ${requestQueue.length}`);
      const result = await requestFn(); // Execute the actual API call
      resolve(result); // Resolve the original apiClient promise
    } catch (error) {
      reject(error); // Reject the original apiClient promise
    }
  }

  isProcessingQueue = false;
  logDev("[Queue] Finished processing queue.");
}

// --- Caching Mechanism ---
const CACHE_DURATION_MS = 5 * 1000; // 5 seconds

interface CacheEntry<T> {
  data: T | { data: T[] }; // Can be single item or list for allPages
  timestamp: number;
}

// Cache for GET requests
const getCache = new Map<string, CacheEntry<any>>();

// Timestamp of the last POST or PUT operation
let lastWriteOperationTimestamp = 0;

// --- Cookie Reading and Redirection Logic ---

// Helper to get a specific cookie by name
function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i=0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

interface UserData {
  email?: string;
  appConfig?: string;
}

// Function to parse the userData cookie
function parseUserDataCookie(): UserData | null {
  const userDataString = getCookie('userData');
  if (!userDataString) {
    return null;
  }
  try {
    const userData = JSON.parse(userDataString);
    // Check if the parsed object is valid and has expected properties
    // We consider data "empty" if email or appConfig are missing or empty strings
    if (typeof userData === 'object' && userData !== null &&
        (userData.email && userData.email.trim() !== '') &&
        (userData.appConfig && userData.appConfig.trim() !== '')) {
      return userData;
    }
    // Data exists but is empty or malformed in terms of required fields
    return null;
  } catch (e) {
    errorDev("Error parsing userData cookie:", e);
    return null; // Malformed JSON
  }
}

// Function to handle redirection
function redirectToOnboarding() {
  warnDev("User data cookie missing or invalid. Redirecting to onboarding.");
  // Ensure we are in a browser environment before attempting to redirect
  if (typeof window !== 'undefined') {
    window.location.href = '/onboarding.html';
  }
  // Throw an error to stop further execution in the current call stack.
  // This error will cause any pending API client promises to reject.
  throw new Error("Redirecting to onboarding. Further API calls aborted.");
}

// Initialize headers based on cookie data
let xDemoHeaderValue: string = 'alt'; // Default value
let xUserIdHeaderValue: string = 'tester'; // Default value

// Attempt to read user data from the cookie when the module loads
const userDataFromCookie = parseUserDataCookie();

if (!userDataFromCookie) {
  // If cookie is missing or has empty/invalid data, redirect.
  // The `redirectToOnboarding` function will throw an error,
  // which prevents the rest of this module from initializing
  // and any subsequent API calls from being made.
  redirectToOnboarding();
} else {
  // If valid data is found, use it for the headers
  xDemoHeaderValue = userDataFromCookie.appConfig!;
  xUserIdHeaderValue = userDataFromCookie.email!;
}

// Define default headers using the values derived from the cookie
const defaultHeaders = {
  'Content-Type': 'application/json',
  'X-Demo': xDemoHeaderValue,
  'X-User-ID': xUserIdHeaderValue,
  'X-Worker-ID': process.env.REACT_APP_API_WID,
  'Authorization': process.env.REACT_APP_API_ATOKEN ? ((xDemoHeaderValue != 'tshirt_printing_store') ? process.env.REACT_APP_API_ATOKEN.replace('Demo', 'DemoAlt') : process.env.REACT_APP_API_ATOKEN) : ''
};

// --- apiClient function ---

interface ApiClientOptions extends RequestInit {
  allPages?: boolean; // New option to fetch all pages
  pageSize?: number; // Optional page size for allPages mode when allPages is true
  bypassCache?: boolean; // NEW: Option to bypass the cache
  downloadCsv?: boolean; // NEW: Option to download data as CSV
}

// Define a generic ListResponse structure for pagination
interface ListResponse<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

/**
 * Generic API client function with retry mechanism for 429 errors,
 * global rate limiting, and multi-page fetching for lookup data.
 * Requests are queued and processed sequentially.
 * @param endpoint The API path, e.g., '/currencies'.
 * @param options Fetch options, including `allPages` to fetch all data.
 * @returns The JSON response data. If `allPages` is true, returns `{ data: T[] }`.
 */
export async function apiClient<T>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T | { data: T[] } | void> { // MODIFIED: Added `| void` to return type
  // Return a new Promise immediately, which will be resolved/rejected
  // when the actual API call (from the queue) completes.
  return new Promise((resolve, reject) => {
    // Wrap the actual API call logic into a function to be queued
    const queuedApiCall = async (): Promise<T | { data: T[] } | void> => { // MODIFIED: Added `| void`
      const { allPages, pageSize = 100, method, bypassCache, downloadCsv, ...restFetchOptions } = options; // MODIFIED: Destructure downloadCsv
      const normalizedMethod = (method || 'GET').toUpperCase();

      const executeFetchWithRetry = async (url: string, currentFetchOptions: RequestInit): Promise<Response> => {
        for (let retries = 0; retries <= MAX_RETRIES; retries++) {
          try {
            logDev(`[API Call] Fetching ${url} (Attempt ${retries + 1}/${MAX_RETRIES + 1})`);
            const response = await fetch(url, {
              ...currentFetchOptions,
              headers: {
                ...defaultHeaders, // Use the globally defined defaultHeaders
                ...currentFetchOptions.headers,
                // For CSV download, we might not want 'application/json' content type
                ... (downloadCsv ? { 'Content-Type': 'text/csv' } : {}), // NEW: Adjust Content-Type for CSV
              },
            });

            if (response.status === 429) {
              if (retries < MAX_RETRIES) {
                const retryAfterHeader = response.headers.get('Retry-After');
                let delayMs = DEFAULT_RETRY_AFTER_MS;

                if (retryAfterHeader) {
                  const parsedSeconds = parseInt(retryAfterHeader, 10);
                  if (!isNaN(parsedSeconds)) {
                    delayMs = parsedSeconds * 1000; // Convert seconds to milliseconds
                  } else {
                    const date = new Date(retryAfterHeader);
                    if (!isNaN(date.getTime())) {
                      const timeToWait = date.getTime() - Date.now();
                      delayMs = Math.max(DEFAULT_RETRY_AFTER_MS, timeToWait);
                    }
                  }
                }
                warnDev(`[API Call] Rate limit hit for ${endpoint}. Retrying in ${delayMs / 1000} seconds... (Attempt ${retries + 1}/${MAX_RETRIES + 1})`);

                globalThrottleUntilTime = Math.max(globalThrottleUntilTime, Date.now() + delayMs);
                await new Promise(resolve => setTimeout(resolve, delayMs));
                continue; // Retry the request
              } else {
                const errorBody: SimpleError = await response.json().catch(() => ({
                  message: `HTTP error! Status: ${response.status}`,
                }));
                // NEW: Display global error before throwing
                displayGlobalError(errorBody.message || `Rate limit exceeded after ${MAX_RETRIES} retries.`, response.status);
                throw new ApiError(
                  response.status,
                  errorBody.message || `Rate limit exceeded after ${MAX_RETRIES} retries.`,
                  errorBody
                );
              }
            }
            return response; // Return successful response
          } catch (error) {
            if (error instanceof ApiError) {
              // If it's already an ApiError (e.g., from 429 above), re-throw it,
              // but ensure it was displayed globally if it wasn't already.
              if (!error.message.includes("Rate limit exceeded")) { // Avoid double-display for 429
                 displayGlobalError(error.message, error.status);
              }
              throw error;
            }
            errorDev(`[API Call] Network or unexpected error for ${url}:`, error);
            const errorMessage = `Network or unexpected error during fetch to ${url}: ${(error as Error).message}`;
            // NEW: Display global error for network issues
            displayGlobalError(errorMessage, 0); // Use status 0 for network errors
            throw new ApiError(0, errorMessage);
          }
        }
        const finalErrorMessage = `Failed to fetch ${url} after ${MAX_RETRIES + 1} attempts due to unknown reasons.`;
        displayGlobalError(finalErrorMessage, 500); // NEW: Display global error for final failure
        throw new ApiError(500, finalErrorMessage);
      };

      // --- Determine Cache Key and Handle Cache Read for GET requests ---
      let cacheKey: string | undefined;
      if (normalizedMethod === 'GET') {
        // NEW: Check bypassCache option
        if (bypassCache || downloadCsv) { // MODIFIED: Bypass cache for CSV downloads
          logDev(`[Cache] Bypassing cache for GET ${endpoint} due to bypassCache or downloadCsv option.`);
          // No cache lookup, proceed directly to fetch
        } else {
          // Original cache logic
          // Construct the cache key based on the intended logical request
          if (allPages) {
            // For allPages, the cache key represents the aggregated result
            cacheKey = `${BASE_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}allPages=true&pageSize=${pageSize}`;
          } else {
            // For single GET, the cache key is the exact URL that would be fetched
            cacheKey = `${BASE_URL}${endpoint}`;
          }

          const cachedEntry = getCache.get(cacheKey);
          if (cachedEntry) {
            const isGloballyInvalidated = cachedEntry.timestamp < lastWriteOperationTimestamp;
            const isLocallyStale = (Date.now() - cachedEntry.timestamp) > CACHE_DURATION_MS;

            if (!isGloballyInvalidated && !isLocallyStale) {
              logDev(`[Cache] Using cached response for GET ${cacheKey}`);
              return cachedEntry.data; // Return cached data immediately
            } else {
              logDev(`[Cache] Cache for GET ${cacheKey} is stale (globally invalidated: ${isGloballyInvalidated}, locally stale: ${isLocallyStale}).`);
              getCache.delete(cacheKey); // Clean up stale entry
            }
          }
        }
      }

      // --- Handle Cache Invalidation for POST/PUT requests ---
      if (normalizedMethod === 'POST' || normalizedMethod === 'PUT' || normalizedMethod === 'DELETE') {
        logDev(`[Cache] POST/PUT request detected for ${endpoint}. Invalidating all GET caches.`);
        lastWriteOperationTimestamp = Date.now();
        getCache.clear();
      }

      // --- Construct the URL for the actual fetch ---
      let requestUrl = `${BASE_URL}${endpoint}`; // Start with the base URL + endpoint

      if (downloadCsv) {
        // For CSV download, we want all data, so remove any pagination and add _csv=1
        const urlObj = new URL(requestUrl); // Use URL object for robust parameter manipulation
        urlObj.searchParams.delete('page');
        urlObj.searchParams.delete('page_size');
        urlObj.searchParams.set('_csv', '1');
        requestUrl = urlObj.toString(); // Get the full URL string
      } else if (normalizedMethod === 'GET' && !allPages) {
        // For single page GET, the endpoint might already contain query params (e.g., from a search filter)
        // No modification needed here, `requestUrl` is fine.
      }
      // Note: The `allPages` logic below handles its own paginated URL construction in a loop.


      // --- Perform the actual fetch ---
      if (downloadCsv) { // NEW: Handle CSV download
        const response = await executeFetchWithRetry(requestUrl, {
          ...restFetchOptions,
          method: 'GET', // CSV export is always GET
        });

        if (!response.ok) {
          const errorBody: SimpleError = await response.json().catch(() => ({
            message: `HTTP error! Status: ${response.status}`,
          }));
          displayGlobalError(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
          throw new ApiError(response.status, errorBody.message, errorBody);
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'data_export.csv'; // Default filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;"]+)/i); // More robust regex
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/^UTF-\d''/, ''));
          }
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        resolve(); // Resolve with void for CSV download
        return; // Exit early
      }

      let resultData: T | { data: T[] };

      if (allPages && normalizedMethod === 'GET') {
        // Special handling for allPages GET, which was not cached
        let allCollectedData: T[] = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          const paginatedEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}page=${currentPage}&page_size=${pageSize}`;
          const url = `${BASE_URL}${paginatedEndpoint}`;

          try {
            const response = await executeFetchWithRetry(url, restFetchOptions);

            if (!response.ok) {
              const errorBody: SimpleError = await response.json().catch(() => ({
                message: `HTTP error! Status: ${response.status}`,
              }));
              // NEW: Display global error before throwing
              displayGlobalError(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
              throw new ApiError(response.status, errorBody.message, errorBody);
            }
            const data: ListResponse<T> = await response.json();
            allCollectedData = allCollectedData.concat(data.data);
            totalPages = data.pagination.total_pages;
            currentPage++;
          } catch (err) {
            // If an error occurs during pagination, throw it
            throw err;
          }
        } while (currentPage <= totalPages);
        resultData = { data: allCollectedData };
      } else {
        // Normal single request flow for GET (not allPages, not cached) or other methods (POST, PUT, DELETE)
        const response = await executeFetchWithRetry(requestUrl, { // MODIFIED: Use requestUrl
          ...restFetchOptions,
          method: normalizedMethod, // Ensure method is passed
        });

        if (!response.ok) {
          const errorBody: SimpleError = await response.json().catch(() => ({
            message: `HTTP error! Status: ${response.status}`,
          }));
          // NEW: Display global error before throwing
          displayGlobalError(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
          throw new ApiError(
            response.status,
            errorBody.message,
            errorBody
          );
        }

        // Handle 204 No Content for delete operations
        if (response.status === 204) {
          resultData = {} as T;
        } else {
          resultData = await response.json() as T;
        }
      }

      // --- Cache Write Logic for successful GET requests ---
      if (normalizedMethod === 'GET' && cacheKey && !bypassCache && !downloadCsv) { // MODIFIED: Only cache if downloadCsv is not true
        getCache.set(cacheKey, { data: resultData, timestamp: Date.now() });
        logDev(`[Cache] Stored new response for GET ${cacheKey}`);
      }

      resolve(resultData);
    };

    // Add the API call to the queue
    requestQueue.push({ requestFn: queuedApiCall, resolve, reject });
    logDev(`[Queue] Added request for ${endpoint}. Queue size: ${requestQueue.length}`);
    // Start processing the queue if it's not already running
    processQueue();
  });
}